"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RoomSession = void 0;
const analytics_1 = require("@streem/analytics");
const logger_1 = require("@streem/logger");
const toolbox_1 = require("@streem/toolbox");
const detectrtc_1 = __importDefault(require("detectrtc"));
const operators_1 = require("rxjs/operators");
const logger_2 = require("../../util/logger");
const ar_controller_1 = require("../controllers/ar_controller");
const bookmark_controller_1 = require("../controllers/bookmark_controller");
const geolocation_controller_1 = require("../controllers/geolocation_controller");
const invitation_controller_1 = require("../controllers/invitation_controller");
const media_controller_1 = require("../controllers/media_controller/media_controller");
const notes_controller_1 = require("../controllers/notes_controller");
const personalization_controller_1 = require("../controllers/personalization_controller");
const room_controller_1 = require("../controllers/room_controller");
const streemshot_controller_1 = require("../controllers/streemshot_controller");
const tools_controller_1 = require("../controllers/tools_controller");
const user_controller_1 = require("../controllers/user_controller");
const room_wall_1 = require("../wall/room_wall");
const wall_sync_builder_1 = require("../wall/wall_sync_builder");
const camera_controller_1 = require("../controllers/camera_controller");
const detection_controller_1 = require("../controllers/detection_controller");
const host_controller_1 = require("../controllers/host_controller");
const track_provider_builder_1 = require("../../util/track_provider_builder");
const recording_controller_1 = require("../controllers/recording_controller");
class RoomSession {
    static buildWallSyncClient(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new wall_sync_builder_1.RoomWallSyncBuilder(config.roomId, config.localDeviceId, config.localUserId, false).build();
        });
    }
    constructor(config, wallSyncClient) {
        var _a;
        this.config = config;
        this.subscriptionManager = new toolbox_1.SubscriptionManager();
        logger_2.logger.info(`Creating new RoomSession for roomId=${config.roomId}`);
        this.callSettings = config.callSettings;
        this.roomId = config.roomId;
        this.roomWall = new room_wall_1.RoomWall(Object.assign(Object.assign({}, config), { alive: true, wallSyncClient }));
        /** Setup controllers */
        this.streemshots = new streemshot_controller_1.StreemshotController(this.controllerConfig);
        this.detections = new detection_controller_1.DetectionController(this.controllerConfig);
        this.user = new user_controller_1.UserController(this.controllerConfig);
        this.invitationController = new invitation_controller_1.InvitationController(this.controllerConfig);
        this.geolocation = new geolocation_controller_1.GeolocationController(this.controllerConfig);
        this.bookmarks = new bookmark_controller_1.BookmarkController(this.controllerConfig);
        this.personalizations = new personalization_controller_1.PersonalizationController(this.controllerConfig);
        this.room = new room_controller_1.RoomController(this.controllerConfig);
        this.note = new notes_controller_1.NoteController(this.controllerConfig);
        this.camera = new camera_controller_1.CameraController(this.controllerConfig);
        this.recording = new recording_controller_1.RecordingController(this.controllerConfig);
        const observables = {
            remoteParticipantCapabilities: this.room.remoteParticipantCapabilities,
        };
        this.media = new media_controller_1.MediaController(this.controllerConfig, observables, (0, track_provider_builder_1.buildTrackProvider)(this.callSettings, observables, (_a = config.isHostSession) !== null && _a !== void 0 ? _a : false, this.roomId));
        this.tools = new tools_controller_1.ToolsController(Object.assign(Object.assign({}, this.controllerConfig), { mediaController: this.media }));
        this.ar = new ar_controller_1.ARController(Object.assign(Object.assign({}, this.controllerConfig), { mediaController: this.media }));
        this.hostUser = new host_controller_1.HostController(this.controllerConfig);
        // Logs
        logger_2.logger.setContextValue(logger_1.contextValues.ROOM_ID, config.roomId.toString());
        logger_2.logger.setContextValue(logger_1.contextValues.ROOM_PARTICIPANTS, {
            localUser: config.localUserId.toString(),
            localDeviceId: config.localDeviceId.toString(),
            remoteUser: config.remoteUserId.toString(),
            remoteDeviceId: config.remoteDeviceId
                ? config.remoteDeviceId.toString()
                : 'no device id found',
        });
        this.connectionUnsubscriber = this.monitorConnectionStatus();
        this.monitorLocalUserRoomPerformance();
    }
    get controllerConfig() {
        return {
            roomWall: this.roomWall,
            context: {
                localUserId: this.config.localUserId,
                localDeviceId: this.config.localDeviceId,
                remoteUserId: this.config.remoteUserId,
                remoteDeviceId: this.config.remoteDeviceId,
                roomId: this.config.roomId,
                callSettings: this.config.callSettings,
            },
        };
    }
    /**
     * Client initiated way to end the call.
     */
    leaveRoom() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_2.logger.info('Client Initiated leaveRoom()');
            yield this.room.leaveRoom();
            yield this.endSession();
        });
    }
    /**
     * Internal clean up of the session
     */
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            this.subscriptionManager.unsubscribeAll();
            try {
                yield this.roomWall.disconnect();
            }
            catch (e) {
                logger_2.logger.error('Failed to disconnect Wall', e);
            }
            try {
                yield this.media.disconnect();
            }
            catch (e) {
                logger_2.logger.error('Failed to disconnect track controller', e);
            }
            if (this.connectionUnsubscriber) {
                this.connectionUnsubscriber();
            }
            clearTimeout(this.roomPerformanceTimer);
            logger_2.logger.info(`The Room session has ended ${this.roomId}`);
            logger_2.logger.removeContextValue(logger_1.contextValues.ROOM_ID);
            logger_2.logger.removeContextValue(logger_1.contextValues.ROOM_PARTICIPANTS);
            logger_2.logger.removeContextValue(logger_1.contextValues.ROOM_PERFORMANCE);
            logger_2.logger.removeContextValue(logger_1.contextValues.WEBRTC);
            logger_2.logger.removeContextValue(logger_1.contextValues.TRACK_PROVIDER);
            logger_2.logger.removeContextValue(logger_1.contextValues.AUDIO_TRACK_SETTINGS);
            this.config.onEnd();
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            logger_2.logger.info(`The Room session was created ${this.roomId}`);
            yield this.roomWall.connect();
            yield this.user.addLocalUserToWall();
            // This is where we publish the camera subjects this client is capable of sending. This will inherit from whichever RoomSession superclass is instantiated.
            yield this.room.sendClientCapabilities(this.constructor.canSendCameraSubjects);
            detectrtc_1.default.load(() => {
                logger_2.logger.setContextValue(logger_1.contextValues.WEBRTC, () => detectrtc_1.default);
            });
            this.subscriptionManager.add(this.status.pipe((0, operators_1.filter)(status => status === 'DISCONNECTED')).subscribe({
                next: () => __awaiter(this, void 0, void 0, function* () {
                    logger_2.logger.info('RoomSessionStatus status changed to DISCONNECTED.  Leaving Room');
                    yield this.leaveRoom();
                }),
                error: error => logger_2.logger.error('Received an error RoomSessionStatus of DISCONNECTED', error),
            }));
        });
    }
    monitorConnectionStatus() {
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
        const connection = 
        // @ts-ignore Experimental API
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!connection) {
            logger_2.logger.info('Network Information API not enabled for this browser.');
            return;
        }
        let type = connection.effectiveType;
        const roomId = this.roomId;
        function updateConnectionStatus() {
            logger_2.logger.info(`Network Connection changed. ${type} -> ${connection.effectiveType} | RTT: ${connection.rtt} | Downlink: ${connection.downlink}`);
            let connectionType;
            switch (connection.type) {
                case 'wifi':
                    connectionType = 'WIFI';
                    break;
                // @ts-ignore this is a valid value. DOM-lib is wrong: https://wicg.github.io/netinfo/
                case 'wimax':
                    connectionType = 'WIFI';
                    break;
                case 'ethernet':
                    connectionType = 'WIFI';
                    break;
                case 'cellular':
                    connectionType = 'CELLULAR';
                    break;
                default:
                    connectionType = 'INVALID';
            }
            (0, analytics_1.recordConnectionStatusChanged)(roomId.toString(), connection.rtt, connectionType);
            type = connection.effectiveType;
        }
        connection.addEventListener('change', updateConnectionStatus);
        return () => connection.removeEventListener('change', updateConnectionStatus);
    }
    monitorLocalUserRoomPerformance() {
        const roomPerformance = {
            url: document.location.href,
            referrer: document.referrer,
            cookie: navigator.cookieEnabled ? document.cookie : 'disabled',
            language: navigator.language,
            hasFocus: document.hasFocus(),
            readyState: document.readyState,
            pageAgeInSeconds: Math.round(performance.now() / 1000),
            heap: (() => {
                let heap;
                try {
                    heap =
                        // @ts-ignore Moz does not support performance.memory
                        Math.round(performance.memory.usedJSHeapSize / 1048576).toString() + 'MB';
                }
                catch (error) {
                    heap = 'unknown';
                }
                return heap;
            })(),
            heapPercent: (() => {
                let heapPercent;
                try {
                    heapPercent =
                        Math.round(
                        // @ts-ignore Moz does not support performance.memory
                        (performance.memory.usedJSHeapSize /
                            // @ts-ignore Moz does not support performance.memory
                            performance.memory.jsHeapSizeLimit) *
                            100).toString() + '%';
                }
                catch (error) {
                    heapPercent = 'unknown';
                }
                return heapPercent;
            })(), // Percent of heap used
        };
        logger_2.logger.setContextValue(logger_1.contextValues.ROOM_PERFORMANCE, roomPerformance);
        this.roomPerformanceTimer = window.setTimeout(() => this.monitorLocalUserRoomPerformance(), 5000);
    }
}
exports.RoomSession = RoomSession;
RoomSession.canSendCameraSubjects = [];
//# sourceMappingURL=room_session.js.map